import React, { createContext, useState, useEffect, useLayoutEffect, useCallback, useContext, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { getDefaultDropdownOptions } from '../utils/firestore';
import { useFormContext } from './FormContext';
import { useFieldMetadata } from './FieldMetadataContext';
import { useAuth } from './AuthContext';
import { safeSetItem, safeGetItem } from '../utils/storageUtils';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { decompressData } from '../utils/decompressionUtils';

// version-aware blob selection (draftOverlay vs root) aligned with other contexts
import { resolveDropdownOptions, getFormVersionSnapshot } from '../utils/formVersionResolver';

// org-managed dropdown runtime resolution
import {
  resolveManagedDropdownOptionsMapForForm,
  mergeLocalAndManagedDropdownOptions,
  formContainsManagedDropdowns,
} from '../utils/managedFieldRuntime';

import {
  isOrgManagedDropdownInstance,
} from '../utils/managedFieldInstances';

const MultiAnswerFieldContext = createContext();

export const useMultiAnswerField = () => useContext(MultiAnswerFieldContext);

// Synchronous storage hydration helper
//
// PURPOSE
//  • Lets MultiAnswerFieldContext read already-seeded dropdown options
//    before first paint
//  • Works for both:
//      - local dropdownOptions
//      - org-managed dropdownOptions pre-seeded by FormLayoutContext
//
// IMPORTANT
//  • This helper is intentionally storage-only and side-effect free
//  • It does NOT fetch Firestore
//  • It does NOT resolve managed runtime options directly
//  • It only reads the current authoritative local/session cache
const readDropdownOptionsFromStorage = ({
  formId,
  effectiveViewMode,
}) => {
  if (!formId) return {};

  try {
    const modeKey = `form_${formId}_dropdownOptions__${effectiveViewMode || 'published'}`;
    const legacyKey = `form_${formId}_dropdownOptions`;

    const stored =
      safeGetItem(modeKey) ||
      safeGetItem(legacyKey) ||
      {};

    return stored && typeof stored === 'object' ? stored : {};
  } catch (error) {
    console.warn('[MultiAnswerFieldContext] Failed to synchronously read dropdown options from storage:', error);
    return {};
  }
};

/**
 * Normalize one dropdown option before it is persisted to the form-local
 * dropdownOptions map.
 *
 * ENTERPRISE CONTRACT
 *  • Preserve the existing local dropdown schema:
 *      - optionId
 *      - label
 *      - value
 *      - hint
 *      - dep
 *      - order
 *
 *  • Preserve approved system metadata:
 *      - systemValue
 *
 * WHY
 *  • optionId remains the stable answer/dependency identity.
 *  • label remains the admin/user-facing display string.
 *  • value remains the existing form/export value behavior.
 *  • systemValue is the platform-owned machine meaning used by system-backed
 *    dropdowns such as Asset Type.
 *
 * IMPORTANT
 *  • Do NOT blindly persist every key from option objects.
 *  • Form Lists / Spreadsheet rows can contain UI-only fields such as rowId,
 *    dependency, dirty, isNew, and internal row flags.
 *  • Persisting those would leak workspace state into published form schema.
 *  • This helper is intentionally allowlist-based.
 */
const normalizeDropdownOptionForPersistence = (option = {}) => {
  const opt =
    option && typeof option === 'object'
      ? option
      : {};

  const normalized = {
    // Preserve existing behavior exactly: callers are still responsible for
    // generating optionId before saving when needed.
    optionId: opt.optionId,
    label: opt.label || '',
    value: opt.value || '',
    hint: opt.hint || '',
    dep: opt.dep || '',
    order: opt.order,
  };

  // ---------------------------------------------------------------------------
  // System option identity
  // ---------------------------------------------------------------------------
  // Used by system-backed dropdowns such as Asset Type.
  //
  // Example:
  //   label       = "Product Screenshot"    // admin-facing
  //   value       = "product_screenshot"    // existing value/export behavior
  //   optionId    = "uuid..."               // answer/dependency identity
  //   systemValue = "screenshot"            // platform machine meaning
  //
  // Backward compatibility:
  // Existing options without systemValue remain valid. Backend catalog building
  // will continue to fall back to option.value.
  // ---------------------------------------------------------------------------
  const systemValue =
    opt.systemValue != null
      ? String(opt.systemValue).trim()
      : '';

  if (systemValue) {
    normalized.systemValue = systemValue;
  }

  return normalized;
};

const normalizeDropdownOptionsForPersistence = (optionsData) => {
  if (!Array.isArray(optionsData)) return [];

  return optionsData.map((option) =>
    normalizeDropdownOptionForPersistence(option)
  );
};

export const MultiAnswerFieldProvider = ({ children }) => {
  const { formId, isPublished } = useFormContext(); 
  const { organizationId } = useAuth() || {};
  const { fieldsMetadata, isLoading: isFieldsMetadataLoading } = useFieldMetadata();

 const [dropdownOptions, _setDropdownOptions] = useState(() => {
   // ENTERPRISE:
   // Do not read dropdown options from storage until a real formId exists.
   //
   // Previous behavior attempted to read `form_null_dropdownOptions` during the
   // initial render before FormContext had resolved the route formId. That created
   // unnecessary storage work and console noise on public/embed routes without
   // providing a useful runtime value.
   //
   // The real first-paint hydration path is the useLayoutEffect below, which runs
   // once formId and effectiveViewMode are known and calls
   // readDropdownOptionsFromStorage({ formId, effectiveViewMode }).
   //
   // This keeps:
   //  • builder flows intact
   //  • saved draft flows intact
   //  • published viewer routes intact
   //  • embed routes lighter and quieter
   return {};
 });

 // Guard: keep previous ref if next value is deep‑equal
 const setDropdownOptions = useCallback(
   (producer) => {
     _setDropdownOptions((prev) => {
       const next =
         typeof producer === 'function' ? producer(prev) : producer;
       return isEqual(prev, next) ? prev : next;
     });
   },
   []
 );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Org-level SmartLists invalidation token
  // ---------------------------------------------------------------------------
  // PURPOSE
  //  • Forces managed dropdown runtime resolution to rerun when the organization’s
  //    SmartLists are updated elsewhere (for example, in another open tab/page).
  //  • Keeps already-open forms/builders in sync without a full page refresh.
  // ---------------------------------------------------------------------------
  const [smartListsUpdatedAtToken, setSmartListsUpdatedAtToken] = useState(null);

  // ────────────────────────────────────────────────────────────────
  // Route-aware flags (builder vs viewer) – mirrors FormThemeContext
  // ────────────────────────────────────────────────────────────────
  const isPublicPreviewMode = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('isPublicPreview');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value === true;
    } catch (e) {
      return false;
    }
  }, []);

  const isBuilderRoute = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/form-builder');
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Mode-aware dropdownOptions cache keys (aligned with FormLayoutContext)
  // - Builder route: honor builder view mode (auto/published/draftOverlay)
  // - Viewer route (/forms/:id): ALWAYS force published
  // ────────────────────────────────────────────────────────────────
  const builderViewMode = useMemo(() => {
    try {
      if (!formId) return 'auto';
      const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
    } catch (e) {
      return 'auto';
    }
  }, [formId]);

  const effectiveViewMode = useMemo(() => {
    if (isBuilderRoute) return builderViewMode;
    return 'published';
  }, [isBuilderRoute, builderViewMode]);

  // Mode-aware cache keys (we STILL mirror legacy key to avoid breaking older reads during refactor)
  const dropdownOptionsKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownOptions__${effectiveViewMode}`;
  }, [formId, effectiveViewMode]);

  const lastUpdatedKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownLastUpdated__${effectiveViewMode}`;
  }, [formId, effectiveViewMode]);

  // Legacy keys (still used by other not-yet-refactored paths)
  const legacyDropdownOptionsKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownOptions`;
  }, [formId]);

  const legacyLastUpdatedKey = useMemo(() => {
    if (!formId) return null;
    return `form_${formId}_dropdownLastUpdated`;
  }, [formId]);

  // ────────────────────────────────────────────────────────────────
  // Dropdown grouping helpers
  //
  // PURPOSE
  //  • Keep local dropdown behavior exactly as-is
  //  • Prevent org-managed dropdowns from being treated as local fields
  //    during default generation / storage seeding
  //  • Allow managed dropdowns to resolve their options from the central
  //    org-managed registry at runtime
  //
  // IMPORTANT
  //  • Local dropdowns still use:
  //      - storage
  //      - Firestore form blobs
  //      - embedded defaults
  //  • Org-managed dropdowns will use:
  //      - managedFieldRuntime.js
  //      - managed option sets from the organization registry
  // ────────────────────────────────────────────────────────────────
  const allDropdownFields = useMemo(() => {
    return Object.values(fieldsMetadata || {}).filter(
      (field) => field.type === 'single-select' || field.type === 'multi-select'
    );
  }, [fieldsMetadata]);

  const localDropdownFields = useMemo(() => {
    return allDropdownFields.filter(
      (field) => !isOrgManagedDropdownInstance(field)
    );
  }, [allDropdownFields]);

  const managedDropdownFields = useMemo(() => {
    return allDropdownFields.filter((field) =>
      isOrgManagedDropdownInstance(field)
    );
  }, [allDropdownFields]);

  // ────────────────────────────────────────────────────────────────
  // Pre-paint dropdownOptions hydration
  //
  // PURPOSE
  //  • Eliminate first-paint flicker by synchronizing the live in-memory
  //    dropdownOptions state with the current storage snapshot BEFORE paint
  //  • This is especially important for org-managed dropdown fields that are
  //    pre-seeded into storage during FormLayoutContext.handleDrop(...)
  //
  // IMPORTANT
  //  • useLayoutEffect runs after React commits but before the browser paints
  //  • This is the correct source-level fix because MultiAnswerFieldContext owns
  //    the live dropdownOptions state used by the renderer
  //  • The later fetch/overlay effects remain authoritative refresh layers
  // ────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    if (!formId) return;

    const stored = readDropdownOptionsFromStorage({
      formId,
      effectiveViewMode,
    });

    if (!stored || typeof stored !== 'object') return;

    setDropdownOptions((prev) => {
      if (isEqual(prev, stored)) return prev;
      return stored;
    });
  }, [formId, effectiveViewMode, fieldsMetadata, setDropdownOptions]);

  /**
   * Fetch all dropdown options for a form.
   * First checks local/session storage, then Firestore if necessary.
   */
  const fetchDropdownOptions = useCallback(async () => {
    if (!formId) {
      console.warn("[MultiAnswerFieldContext] No formId provided, skipping dropdown options fetch.");
      return;
    }

    setLoading(true);

    // Generate storage keys for dropdown options and timestamps (mode-aware + legacy fallback)
    const activeDropdownOptionsKey = dropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
    const activeLastUpdatedKey = lastUpdatedKey || `form_${formId}_dropdownLastUpdated__published`;

    const legacyKey = legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;
    const legacyUpdatedKey = legacyLastUpdatedKey || `form_${formId}_dropdownLastUpdated`;

    // Step 1: Attempt to retrieve stored dropdown options from session/local storage
    const storedDropdownOptions =
      safeGetItem(activeDropdownOptionsKey) ||
      safeGetItem(legacyKey);

    const localLastUpdated =
      safeGetItem(activeLastUpdatedKey) ||
      safeGetItem(legacyUpdatedKey); // Local last updated timestamp

    if (storedDropdownOptions && Object.keys(storedDropdownOptions).length > 0) {
//      console.log('[MAF:fetchDropdownOptions] USING STORAGE for formId:', formId, {
//        effectiveViewMode,
//        activeKey  : activeDropdownOptionsKey,
//        legacyKey,
//        keys      : Object.keys(storedDropdownOptions),
//        sampleSet : Object.fromEntries(
//          Object.entries(storedDropdownOptions).slice(0, 3) // first 3 UFIs only
//        ),
//      });

      // IMPORTANT:
      // In builder/public-preview we prefer the storage snapshot immediately.
      // This includes org-managed dropdowns that may have been pre-seeded by
      // FormLayoutContext during drag/drop.
      setDropdownOptions((prev) => {
        return isEqual(prev, storedDropdownOptions) ? prev : storedDropdownOptions;
      });

      // ────────────────────────────────────────────────────────────
      // Builder/public-preview: allow storage to short-circuit.
      // Viewer (/forms/:formId): DO NOT return here – fall through
      // so Firestore can refresh stale dropdowns (e.g., AUF).
      // ────────────────────────────────────────────────────────────
      if (isBuilderRoute || isPublicPreviewMode) {
        setLoading(false);
        return; // ✅ Use stored dropdown options, skip Firestore fetch (builder only)
      }
      // Viewer: keep loading=true and continue to Firestore fetch
    }

    // Step 2: If no stored data, OR we are on a viewer route, fetch from Firestore
//    console.log("[MultiAnswerFieldContext] No dropdown options found in storage. Fetching from Firestore...");

    try {
      const formRef = doc(db, 'forms', formId);
      const formDoc = await getDoc(formRef);

      if (!formDoc.exists()) {
        console.warn(`[MultiAnswerFieldContext] No dropdown options found in Firestore for formId: ${formId}`);
        setLoading(false);
        return;
      }

      const formData = formDoc.data() || {};

      // NEW: version-aware resolve for dropdownOptions (draftOverlay vs root)
      const versionSnap = getFormVersionSnapshot(formData);
      const resolvedOpts = resolveDropdownOptions(formData, { viewMode: effectiveViewMode, debug: true });

//      console.log('[MAF:fetchDropdownOptions] version snapshot:', versionSnap);
//      console.log('[MAF:fetchDropdownOptions] resolved dropdownOptions source:', resolvedOpts);

      const compressedDropdownOptions = resolvedOpts?.value;

      // Use an updated timestamp that matches the source we chose
      // - overlay: draftOverlaySavedAt / draftOverlay.savedAt
      // - root: lastModified
      const firestoreLastUpdated = (() => {
        try {
          if (resolvedOpts?.source === 'draftOverlay') {
            const t =
              formData?.draftOverlaySavedAt ||
              formData?.draftOverlay?.savedAt ||
              null;
            if (t && typeof t.toDate === 'function') return t.toDate().toISOString();
            if (t instanceof Date) return t.toISOString();
          }
          const lm = formData?.lastModified;
          if (lm && typeof lm.toDate === 'function') return lm.toDate().toISOString();
          if (lm instanceof Date) return lm.toISOString();
        } catch (e) {}
        return null;
      })();

      if (!compressedDropdownOptions) {
        console.warn(
          `[MultiAnswerFieldContext] No resolved dropdown options blob found for formId: ${formId} (viewMode=${effectiveViewMode})`
        );
        setLoading(false);
        return;
      }


      // Step 3: Timestamp Validation - If local data is newer, skip Firestore overwrite
      // BUT ONLY in builder / public-preview. Viewer should always trust Firestore.
      if (
        (isBuilderRoute || isPublicPreviewMode) &&
        localLastUpdated &&
        firestoreLastUpdated &&
        localLastUpdated > firestoreLastUpdated
      ) {
        console.warn(
          `[MultiAnswerFieldContext] Local dropdown options are newer than Firestore. Skipping Firestore overwrite.`, 
          { localLastUpdated, firestoreLastUpdated }
        );
        setLoading(false);
        return;
      }

      // Step 4: Decompress Firestore dropdown options
//      console.log(`[MAF:fetchDropdownOptions] USING FIRESTORE for formId: ${formId} → decompressing...`, {
//        compressedLength: compressedDropdownOptions.length,
//        firestoreLastUpdated,
//      });
      const decompressedDropdownOptions = await decompressData(compressedDropdownOptions);

//      console.log('[MAF:fetchDropdownOptions] Decompressed dropdownOptions snapshot:', {
//        keys      : Object.keys(decompressedDropdownOptions || {}),
//        sampleSet : Object.fromEntries(
//          Object.entries(decompressedDropdownOptions || {}).slice(0, 3)
//        ),
//      });

      // Store decompressed options in state and local storage
      setDropdownOptions(decompressedDropdownOptions);

      // Cache mode-aware (authoritative for this view mode)
      safeSetItem(activeDropdownOptionsKey, decompressedDropdownOptions);
      safeSetItem(activeLastUpdatedKey, firestoreLastUpdated || new Date().toISOString());

      // Mirror legacy during refactor (keeps older code paths stable)
      safeSetItem(legacyKey, decompressedDropdownOptions);
      safeSetItem(legacyUpdatedKey, firestoreLastUpdated || new Date().toISOString());


    } catch (err) {
      console.error("[MultiAnswerFieldContext] Error fetching dropdown options from Firestore:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    formId,
    isBuilderRoute,
    isPublicPreviewMode,
    effectiveViewMode,
    dropdownOptionsKey,
    lastUpdatedKey,
    legacyDropdownOptionsKey,
    legacyLastUpdatedKey,
  ]);


  /**
   * Save dropdown options to session/local storage.
   * Updates Firestore only when necessary.
   */
  const saveDropdownOptionsToStorage = useCallback((optionsSetUFI, optionsData) => {
    if (!formId || !optionsSetUFI || !optionsData) {
      console.warn("[MultiAnswerFieldContext] Invalid parameters. Skipping save operation.", {
        formId,
        optionsSetUFI,
        hasOptionsData: !!optionsData,
      });
      return;
    }

    // Ensure optionsData is normalized through the central persistence boundary.
    //
    // ENTERPRISE:
    // Do not inline-map dropdown options here.
    // The option persistence contract is centralized in
    // normalizeDropdownOptionsForPersistence(...) so Advanced Settings, Form
    // Lists, and future option editors all preserve the same approved schema.
    const optionsToSave = normalizeDropdownOptionsForPersistence(optionsData);

//    console.log(`[MAF:saveDropdownOptionsToStorage] formId=${formId}, optionsSetUFI=${optionsSetUFI}`, {
//      count: optionsToSave.length,
//      sample: optionsToSave.slice(0, 5),
//    });

    // ────────────────────────────────────────────────────────────────
    // NEW: mode-aware storage keys (aligned with FormLayoutContext)
    // - write to mode-aware key (authoritative for current viewMode)
    // - mirror to legacy key (temporary compatibility during refactor)
    // ────────────────────────────────────────────────────────────────
    const activeKey =
      dropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
    const activeUpdatedKey =
      lastUpdatedKey || `form_${formId}_dropdownLastUpdated__published`;

    const legacyKey =
      legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;
    const legacyUpdatedKey =
      legacyLastUpdatedKey || `form_${formId}_dropdownLastUpdated`;

    // Retrieve existing dropdown options from storage (mode-aware first, then legacy)
    const storedDropdownOptions =
      safeGetItem(activeKey) ||
      safeGetItem(legacyKey) ||
      {};

//    console.log('[MAF:saveDropdownOptionsToStorage] BEFORE merge, storedDropdownOptions keys:', Object.keys(storedDropdownOptions));

    storedDropdownOptions[optionsSetUFI] = optionsToSave;

    // Save the updated dropdown options back to storage (mode-aware + legacy mirror)
    safeSetItem(activeKey, storedDropdownOptions);
    safeSetItem(activeUpdatedKey, new Date().toISOString());

    safeSetItem(legacyKey, storedDropdownOptions);
    safeSetItem(legacyUpdatedKey, new Date().toISOString());

//    console.log('[MAF:saveDropdownOptionsToStorage] AFTER merge, storedDropdownOptions snapshot:', {
//      effectiveViewMode,
//      activeKey,
//      legacyKey,
//      keys      : Object.keys(storedDropdownOptions),
//      sampleSet : Object.fromEntries(
//        Object.entries(storedDropdownOptions).slice(0, 3)
//      ),
//    });

    // Update state
    setDropdownOptions((prev) => ({
      ...prev,
      [optionsSetUFI]: optionsToSave,
    }));
  }, [
    formId,
    effectiveViewMode,
    dropdownOptionsKey,
    lastUpdatedKey,
    legacyDropdownOptionsKey,
    legacyLastUpdatedKey,
  ]);



  /**
   * Generate default dropdown options for new fields
   * If the field is new and no options exist, generate default dropdown options and save them in storage.
   */
  const generateDefaultDropdownOptions = useCallback(() => {
    // IMPORTANT:
    // Only LOCAL dropdown fields should flow through the default-generation path.
    // Org-managed dropdowns resolve from the org-managed option set registry and
    // must not be treated as local dropdowns.
    const dropdownFields = localDropdownFields;

    if (dropdownFields.length === 0) {
//      console.log('[MultiAnswerFieldProvider] No LOCAL dropdown fields found. Skipping default options generation.');
      return;
    }

    let shouldUpdate = false;
    const updatedDropdownOptions = { ...dropdownOptions }; // Clone to avoid direct state mutation

    dropdownFields.forEach((field) => {
      const optionsSetUFI = field.optionsSetUFI || null;

      // Check if options already exist in `dropdownOptions` state or storage
      if (updatedDropdownOptions[optionsSetUFI]?.length > 0) {
//        console.log(
//          `[MultiAnswerFieldProvider] Dropdown options already exist for UFI ${optionsSetUFI} in dropdownOptions state, skipping generation.`
//        );
        return; // Skip generation if options already exist in state
      }

      // Fetch stored options from session/local storage to prevent overwriting
      const activeKey =
        dropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
      const legacyKey =
        legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;

      const storedDropdownOptions =
        safeGetItem(activeKey) ||
        safeGetItem(legacyKey) ||
        {};

      if (storedDropdownOptions[optionsSetUFI]?.length > 0) {
//        console.log(
//          `[MultiAnswerFieldProvider] Dropdown options already exist for UFI ${optionsSetUFI} in storage, skipping generation.`
//        );
        return; // Skip generation if options already exist in storage
      }

      // If no options exist in both state and storage, mark for update and generate default options
      shouldUpdate = true;
//      console.log(
//        `[MultiAnswerFieldProvider] Generating default dropdown options for new field with UFI: ${optionsSetUFI}`
//      );

      // ────────────────────────────────────────────────────────────────
      // ✅ ENTERPRISE: Prefer embedded defaults from field metadata
      // This is critical for "system dropdown fields" like Asset Type:
      // - Asset Type embeds its canonical defaults in metadata.options.options
      // - If we ignore these and call getDefaultDropdownOptions(), we seed
      //   Option 1/2/3 and overwrite system defaults.
      //
      // Rule:
      //   1) If metadata embeds defaults: seed from embedded
      //   2) Else fall back to getDefaultDropdownOptions (Option 1/2/3)
      // ────────────────────────────────────────────────────────────────
      const embeddedDefaults = Array.isArray(field?.options?.options)
        ? field.options.options
        : null;

      // Fetch default options (prefer embedded defaults if present)
      const defaultOptions = embeddedDefaults
        ? { options: embeddedDefaults }
        : getDefaultDropdownOptions(optionsSetUFI);

      // Validate and assign default options
      if (Array.isArray(defaultOptions?.options) && defaultOptions.options.length > 0) {
        updatedDropdownOptions[optionsSetUFI] = defaultOptions.options;
//        console.log(
//          `[MultiAnswerFieldProvider] Fetched and assigned default options for UFI ${optionsSetUFI}`
//        );
      } else {
        console.warn(
          `[MultiAnswerFieldProvider] No valid default options found for UFI ${optionsSetUFI}, assigning empty array.`
        );
        updatedDropdownOptions[optionsSetUFI] = [];
      }
    });

    // Write to storage and update state only if new data was generated
    if (shouldUpdate && JSON.stringify(dropdownOptions) !== JSON.stringify(updatedDropdownOptions)) {
      const activeKey =
        dropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
      const activeUpdatedKey =
        lastUpdatedKey || `form_${formId}_dropdownLastUpdated__published`;

      const legacyKey =
        legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;
      const legacyUpdatedKey =
        legacyLastUpdatedKey || `form_${formId}_dropdownLastUpdated`;

      safeSetItem(activeKey, updatedDropdownOptions);
      safeSetItem(activeUpdatedKey, new Date().toISOString());

      safeSetItem(legacyKey, updatedDropdownOptions);
      safeSetItem(legacyUpdatedKey, new Date().toISOString());
//      console.log(
//        `[MultiAnswerFieldProvider: generateDefaultDropdownOptions] Updated dropdown options saved to storage`,
//        updatedDropdownOptions
//      );
      setDropdownOptions(updatedDropdownOptions);
//      console.log('[MultiAnswerFieldProvider] Dropdown options updated and saved to storage.');
    }
  }, [
    dropdownOptions,
    localDropdownFields,
    formId,
    dropdownOptionsKey,
    lastUpdatedKey,
    legacyDropdownOptionsKey,
    legacyLastUpdatedKey,
  ]);

  /**
   * useEffect to load dropdown options from storage/Firestore when formId or fieldsMetadata changes.
   */
  useEffect(() => {
    if (!formId || isFieldsMetadataLoading || !fieldsMetadata) {
      return;
    }

    if (allDropdownFields.length > 0) {
      fetchDropdownOptions(); // Only load if relevant dropdown fields exist
    }
  }, [formId, fieldsMetadata, isFieldsMetadataLoading, allDropdownFields, fetchDropdownOptions]);

  /**
   * useEffect to handle dropdown data generation for new fields.
   * It will only generate default dropdown options if relevant fields are present.
   */
  useEffect(() => {
    if (!formId || isFieldsMetadataLoading || !fieldsMetadata) {
      return;
    }

    // If this form is published, seed ONLY brand-new option sets (missing in state/storage).
    // We prefer the defaults already embedded by getDefaultFieldMetadata in field metadata
    // (fieldsMetadata[fieldId].options.options). If none exist, we still seed an empty array so
    // the Select UI renders immediately. Existing sets are untouched.
    if (isPublished && (isBuilderRoute || isPublicPreviewMode)) {

      const dropdownFields = localDropdownFields;
      if (dropdownFields.length === 0) return;

      const activeKey =
        dropdownOptionsKey || `form_${formId}_dropdownOptions__published`;
      const legacyKey =
        legacyDropdownOptionsKey || `form_${formId}_dropdownOptions`;

      const stored =
        safeGetItem(activeKey) ||
        safeGetItem(legacyKey) ||
        {};

      let next = { ...(dropdownOptions || {}) };
      let changed = false;

      console.log('[MAF:publishedSeed] Starting seed for published form', {
        formId,
        currentStateKeys  : Object.keys(next),
        storageKeys       : Object.keys(stored),
        dropdownFieldUFIs : dropdownFields.map(f => f.optionsSetUFI),
      });

      dropdownFields.forEach((field) => {
        const setId = field.optionsSetUFI;
        const hasInState   = Array.isArray(next[setId]);   // allow empty array
        const hasInStorage = Array.isArray(stored[setId]); // allow empty array
        if (hasInState || hasInStorage) {
          // Already known → keep existing behavior and skip generation.
          return;
        }

        // Prefer the defaults embedded by getDefaultFieldMetadata at creation time
        const embedded = Array.isArray(field.options?.options) ? field.options.options : null;
        const seed = embedded
          ? normalizeDropdownOptionsForPersistence(embedded)
          : []; // No embedded defaults → seed empty array so Select still renders

//        console.log('[MAF:publishedSeed] Seeding brand-new setId from metadata', {
//          setId,
//          seededCount: seed.length,
//        });

        next[setId] = seed;
        changed = true;
      });

      if (changed && !isEqual(dropdownOptions, next)) {
        const activeUpdatedKey =
          lastUpdatedKey || `form_${formId}_dropdownLastUpdated__published`;
        const legacyUpdatedKey =
          legacyLastUpdatedKey || `form_${formId}_dropdownLastUpdated`;

        safeSetItem(activeKey, next);
        safeSetItem(activeUpdatedKey, new Date().toISOString());

        safeSetItem(legacyKey, next);
        safeSetItem(legacyUpdatedKey, new Date().toISOString());

        setDropdownOptions(next);
//        console.log("[MAF:publishedSeed] Seeded brand-new option sets for published form from field metadata.", {
//          newKeys     : Object.keys(next),
//          originalKeys: Object.keys(dropdownOptions || {}),
//        });
      } else {
//        console.log("[MAF:publishedSeed] No brand-new option sets to seed in published form.");
      }
      return; // Important: do not fall through to draft-only generation
    }

    const dropdownFields = localDropdownFields;

    // only generate default options in builder/public-preview for LOCAL dropdowns.
    // Org-managed dropdowns are resolved separately from the org-managed registry
    // and must not be seeded through the local default-generation path.
    if (dropdownFields.length > 0 && (isBuilderRoute || isPublicPreviewMode)) {
      generateDefaultDropdownOptions(); // Generate default options only if LOCAL dropdown fields are present
    }
  }, [
    formId,
    fieldsMetadata,
    isFieldsMetadataLoading,
    generateDefaultDropdownOptions,
    isPublished,
    dropdownOptions,
    localDropdownFields,
    effectiveViewMode,
    dropdownOptionsKey,
    lastUpdatedKey,
    legacyDropdownOptionsKey,
    legacyLastUpdatedKey,
    isBuilderRoute,
    isPublicPreviewMode,
  ]);

  // ────────────────────────────────────────────────────────────────
  // Org-level SmartLists invalidation subscription
  //
  // PURPOSE
  //  • Listen for canonical SmartLists saves at the organization level
  //  • Trigger a rerun of managed dropdown resolution for already-open forms
  //
  // CONTRACT
  //  • smartListsSaveService writes:
  //      organizations/{orgId}.smartListsUpdatedAt = serverTimestamp()
  //  • We convert that field into a lightweight token so the overlay effect
  //    can depend on it without coupling to Firestore snapshot objects.
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!organizationId) return;

    const orgRef = doc(db, 'organizations', organizationId);

    const unsubscribe = onSnapshot(
      orgRef,
      (snap) => {
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const ts = data?.smartListsUpdatedAt || null;

        let nextToken = null;

        try {
          if (ts && typeof ts.toDate === 'function') {
            nextToken = ts.toDate().toISOString();
          } else if (ts instanceof Date) {
            nextToken = ts.toISOString();
          } else if (typeof ts === 'string') {
            nextToken = ts;
          }
        } catch (e) {
          nextToken = null;
        }

        setSmartListsUpdatedAtToken((prev) => {
          if (prev === nextToken) return prev;

          console.log('[MultiAnswerFieldContext] Detected org-level SmartLists invalidation signal.', {
            organizationId,
            previousToken: prev,
            nextToken,
          });

          return nextToken;
        });
      },
      (err) => {
        console.error('[MultiAnswerFieldContext] Error subscribing to smartListsUpdatedAt:', err);
      }
    );

    return () => {
      try {
        unsubscribe();
      } catch (_) {}
    };
  }, [organizationId]);
  
  /**
   * Org-managed dropdown runtime overlay
   *
   * PURPOSE
   *  • Resolve live org-managed dropdown options centrally
   *  • Merge them into the same runtime dropdownOptions map already used by:
   *      - SingleSelectFieldComponent
   *      - MultiSelectFieldComponent
   *      - FieldRenderer
   *      - Lists / DropdownSyncLogic
   *
   * IMPORTANT
   *  • This does NOT replace local dropdown behavior
   *  • This does NOT persist org-managed options into form-local storage here
   *  • This keeps org-managed definitions centrally governed
   *  • Dependencies remain form-specific because the field instance still uses
   *    its local optionsSetUFI and stable optionIds
   *  • Presets remain form-specific because preset values still bind to the
   *    local field instance / optionIds present in the runtime map
   */
  useEffect(() => {
    let cancelled = false;

    const overlayManagedDropdownOptions = async () => {
      if (!organizationId) return;
      if (!formId) return;
      if (isFieldsMetadataLoading || !fieldsMetadata) return;

      // Fast exit if this form does not contain org-managed dropdown fields
      if (!formContainsManagedDropdowns({ fieldsMetadata })) {
        return;
      }

      try {
        const result = await resolveManagedDropdownOptionsMapForForm({
          organizationId,
          fieldsMetadata,
          now: new Date(),
        });

        if (cancelled) return;

        if (result?.status !== 'success') {
          console.warn('[MultiAnswerFieldContext] Managed dropdown overlay returned non-success result:', result);
          return;
        }

        const managedDropdownOptionsMap = result?.dropdownOptionsMap || {};

        setDropdownOptions((prev) => {
          const merged = mergeLocalAndManagedDropdownOptions({
            localDropdownOptions: prev || {},
            managedDropdownOptionsMap,
          });

          return merged;
        });

        console.log('[MAF:managedOverlay] Applied org-managed dropdown runtime overlay:', {
          organizationId,
          formId,
          managedFieldCount: managedDropdownFields.length,
          managedOptionSetUFIs: Object.keys(managedDropdownOptionsMap || {}),
          diagnostics: result?.diagnostics || null,
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[MultiAnswerFieldContext] Error applying org-managed dropdown overlay:', err);
        setError(err?.message || String(err));
      }
    };

    overlayManagedDropdownOptions();

    return () => {
      cancelled = true;
    };
  }, [
    organizationId,
    formId,
    fieldsMetadata,
    isFieldsMetadataLoading,
    managedDropdownFields,
    setDropdownOptions,
    smartListsUpdatedAtToken,
  ]);

  /**
   * Context value provided to children components
   */
  const contextValue = {
    dropdownOptions,
    setDropdownOptions,
    loading,
    error,
    saveDropdownOptionsToStorage, // Expose save function for manual save trigger
  };

  return (
    <MultiAnswerFieldContext.Provider value={contextValue}>
      {children}
    </MultiAnswerFieldContext.Provider>
  );
};
